import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Megaphone, ArrowUpRight, Lock, CheckCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/components/ui/dialog";

export default function OpenHousePlanCTA() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please share your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/openhouse/request", form);
      if (res.data.success && res.data.url) {
        setResult(res.data);
        toast.success("Your plan is ready");
      } else {
        toast.error(res.data.message || "Something went wrong.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setOpen(false);
    setTimeout(() => {
      setResult(null);
      setForm({ name: "", email: "" });
    }, 300);
  };

  return (
    <div className="bg-paper border border-soft p-10 md:p-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center" data-testid="openhouse-cta">
      <div className="lg:col-span-8">
        <p className="overline flex items-center gap-2">
          <Megaphone size={14} className="text-sand" /> Free for agents
        </p>
        <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl" style={{ fontFamily: "Playfair Display, serif" }}>
          Get the FREE open house marketing plan.
        </h2>
        <p className="mt-4 text-soft max-w-xl">
          The exact framework we use to drive serious foot traffic, capture quality leads, and
          turn open houses into real listings. Yours free — just tell us where to send it.
        </p>
      </div>
      <div className="lg:col-span-4 lg:text-right">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="btn-primary inline-flex" data-testid="openhouse-trigger">
              <Megaphone size={16} className="mr-2" /> Get the plan
            </button>
          </DialogTrigger>
          <DialogContent
            className="bg-paper border border-soft rounded-none max-w-md p-0 gap-0"
            data-testid="openhouse-modal"
          >
            <div className="p-8 md:p-10">
              {result ? (
                <div data-testid="openhouse-success" className="text-center">
                  <CheckCircle2 size={36} className="text-sand mx-auto" strokeWidth={1.25} />
                  <p className="overline mt-6">It's all yours</p>
                  <h3 className="mt-3 text-3xl" style={{ fontFamily: "Playfair Display, serif" }}>
                    Your plan is ready.
                  </h3>
                  <p className="mt-4 text-soft text-sm">
                    Tap below to open the marketing plan. We've also got your details — we'll be in
                    touch with any updates.
                  </p>
                  <a
                    href={result.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={reset}
                    className="btn-primary mt-8 inline-flex"
                    data-testid="openhouse-link"
                  >
                    Open the plan <ArrowUpRight size={14} className="ml-2" />
                  </a>
                </div>
              ) : (
                <form onSubmit={onSubmit} data-testid="openhouse-form">
                  <Lock size={26} className="text-sand" strokeWidth={1.25} />
                  <p className="overline mt-6">Free · No card required</p>
                  <h3 className="mt-3 text-2xl md:text-3xl" style={{ fontFamily: "Playfair Display, serif" }}>
                    Where should we send your plan?
                  </h3>
                  <div className="mt-8 space-y-6">
                    <div>
                      <label className="overline">Name</label>
                      <input
                        className="input-editorial mt-2"
                        value={form.name}
                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                        placeholder="Your full name"
                        data-testid="openhouse-name"
                        required
                      />
                    </div>
                    <div>
                      <label className="overline">Email</label>
                      <input
                        type="email"
                        className="input-editorial mt-2"
                        value={form.email}
                        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                        placeholder="you@brokerage.com"
                        data-testid="openhouse-email"
                        required
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary mt-8 w-full disabled:opacity-50"
                    data-testid="openhouse-submit"
                  >
                    {submitting ? (
                      <><Loader2 size={16} className="animate-spin mr-2" /> Preparing…</>
                    ) : (
                      "Give me the plan"
                    )}
                  </button>
                  <p className="mt-4 text-xs text-soft">No spam. Just the plan and the occasional follow-up.</p>
                </form>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
